<script lang="ts">
  import { Imagenologia, Consultorios, type EstudioCatalogo, type OrdenImagen, type OrdenImagenDetalle, type Consultorio, type Profesional } from '../lib/api';
  import PacientePicker from './PacientePicker.svelte';
  import { session } from '../lib/auth.svelte';
  import { estadoImagenLabel, estadoImagenHint } from '../lib/estudio-estados';

  const IMG_PAQUETES_KEY = 'toscana.img.paquetes.v1';
  type PaqueteImg = { nombre: string; codigos: string[] };

  function loadPaquetesImg(): PaqueteImg[] {
    try {
      const raw = localStorage.getItem(IMG_PAQUETES_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }

  let consultorios = $state<Consultorio[]>([]);
  let profesionales = $state<Profesional[]>([]);
  $effect(() => {
    Consultorios.list().then(c => consultorios = c).catch(() => {});
    Consultorios.profesionales().then(p => profesionales = p).catch(() => {});
  });

  let catalogo = $state<EstudioCatalogo[]>([]);
  let ordenes = $state<OrdenImagen[]>([]);
  let detalle = $state<OrdenImagenDetalle | null>(null);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);
  let busquedaEstudio = $state('');
  let paquetesImg = $state<PaqueteImg[]>(typeof localStorage !== 'undefined' ? loadPaquetesImg() : []);

  let nueva = $state<{ pacienteId: string; pacienteNombre: string; medico: string; indicacion: string; prioridad: 'RUTINA' | 'URGENTE'; consultorioId: string; medicoId: string; pagador: 'PACIENTE' | 'CONSULTORIO' | 'ASEGURADORA'; seleccion: Record<string, boolean> }>(
    { pacienteId: '', pacienteNombre: '', medico: '', indicacion: '', prioridad: 'RUTINA', consultorioId: '', medicoId: '', pagador: 'PACIENTE', seleccion: {} }
  );

  let informe = $state<{ tecnica: string; hallazgos: string; impresion: string; recomendaciones: string; imagenesUrl: string }>(
    { tecnica: '', hallazgos: '', impresion: '', recomendaciones: '', imagenesUrl: '' }
  );

  let nuevoEx = $state<EstudioCatalogo>({ codigo: '', nombre: '', modalidad: 'RAYOS_X', region: '', precio: 0 });
  let mostrarCatalogo = $state(true);
  let editingCodigo = $state<string | null>(null);
  let editPrecio = $state<number | string>(0);

  const MODALIDADES = ['RAYOS_X', 'ULTRASONIDO', 'TOMOGRAFIA', 'RESONANCIA', 'MAMOGRAFIA', 'FLUOROSCOPIA', 'DENSITOMETRIA', 'OTROS'];

  const seleccionados = $derived(Object.keys(nueva.seleccion).filter((k) => nueva.seleccion[k]));
  const totalEstimado = $derived(
    catalogo.filter((e) => e.codigo && nueva.seleccion[e.codigo]).reduce((s, e) => s + (e.precio ?? 0), 0)
  );
  const catalogoFiltrado = $derived(
    !busquedaEstudio.trim() ? catalogo :
    catalogo.filter(e => {
      const q = busquedaEstudio.toLowerCase();
      return (e.nombre ?? '').toLowerCase().includes(q) || (e.codigo ?? '').toLowerCase().includes(q) || (e.modalidad ?? '').toLowerCase().includes(q);
    })
  );
  const puedeAgregarRapido = $derived(
    busquedaEstudio.trim().length >= 2 &&
    !catalogo.some(e => (e.nombre ?? '').toLowerCase() === busquedaEstudio.trim().toLowerCase())
  );

  function slugCodigo(nombre: string, pref: string): string {
    const base = nombre.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toUpperCase().slice(0, 18);
    return `${pref}-${base || 'N'}-${Date.now().toString(36).slice(-4).toUpperCase()}`;
  }

  async function agregarEstudioRapido() {
    const nombre = busquedaEstudio.trim();
    if (!nombre) return;
    error = null;
    try {
      const codigo = slugCodigo(nombre, 'IMG');
      const created = await Imagenologia.crearEstudio({ codigo, nombre, modalidad: 'OTROS', precio: 0 });
      catalogo = await Imagenologia.catalogo();
      const code = created?.codigo ?? codigo;
      nueva.seleccion = { ...nueva.seleccion, [code]: true };
      busquedaEstudio = '';
      info = `«${nombre}» agregado al catálogo y seleccionado.`;
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  function guardarPaqueteImg() {
    const nombre = prompt('Nombre del paquete:');
    if (!nombre?.trim()) return;
    if (seleccionados.length === 0) { error = 'Selecciona al menos un estudio.'; return; }
    const next = [...paquetesImg.filter(p => p.nombre !== nombre.trim()), { nombre: nombre.trim(), codigos: [...seleccionados] }];
    paquetesImg = next;
    localStorage.setItem(IMG_PAQUETES_KEY, JSON.stringify(next));
    info = `Paquete «${nombre.trim()}» guardado.`;
  }

  function aplicarPaqueteImg(p: PaqueteImg) {
    const sel = { ...nueva.seleccion };
    for (const c of p.codigos) sel[c] = true;
    nueva.seleccion = sel;
  }

  async function cargar() {
    loading = true; error = null;
    try {
      [catalogo, ordenes] = await Promise.all([Imagenologia.catalogo(), Imagenologia.ordenes()]);
    } catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  async function abrir(id: string) {
    error = null; info = null;
    try {
      detalle = await Imagenologia.orden(id);
      const o = detalle.orden;
      informe = {
        tecnica: o.tecnica ?? '', hallazgos: o.hallazgos ?? '', impresion: o.impresion ?? '',
        recomendaciones: o.recomendaciones ?? '', imagenesUrl: o.imagenesUrl ?? ''
      };
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function crearOrden(e: SubmitEvent) {
    e.preventDefault();
    error = null; info = null;
    if (!nueva.pacienteId) { error = 'Selecciona un paciente.'; return; }
    if (seleccionados.length === 0) { error = 'Selecciona al menos un estudio.'; return; }
    try {
      const d = await Imagenologia.crearOrden({
        pacienteId: nueva.pacienteId,
        pacienteNombre: nueva.pacienteNombre || undefined,
        medicoSolicitante: nueva.medico || undefined,
        consultorioId: nueva.consultorioId || undefined,
        medicoSolicitanteId: nueva.medicoId || undefined,
        pagador: nueva.pagador,
        indicacionClinica: nueva.indicacion || undefined,
        prioridad: nueva.prioridad,
        estudios: seleccionados,
      });
      detalle = d;
      info = `Orden ${d.orden.numero} creada.`;
      nueva = { pacienteId: '', pacienteNombre: '', medico: '', indicacion: '', prioridad: 'RUTINA', consultorioId: '', medicoId: '', pagador: 'PACIENTE', seleccion: {} };
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function adquirir() {
    if (!detalle) return;
    error = null;
    try {
      detalle = await Imagenologia.adquirir(detalle.orden.id!, informe.imagenesUrl || undefined);
      info = 'Imágenes marcadas como adquiridas.';
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function firmarInforme() {
    if (!detalle) return;
    error = null; info = null;
    if (!informe.hallazgos.trim() || !informe.impresion.trim()) { error = 'Hallazgos e impresión son obligatorios.'; return; }
    try {
      detalle = await Imagenologia.informar(detalle.orden.id!, {
        tecnica: informe.tecnica || undefined,
        hallazgos: informe.hallazgos,
        impresion: informe.impresion,
        recomendaciones: informe.recomendaciones || undefined,
        imagenesUrl: informe.imagenesUrl || undefined,
        radiologo: session.value?.usuario ?? 'Radiólogo',
      });
      info = `Informe firmado. Folio ${detalle.orden.codigoValidacion}.`;
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function entregar() {
    if (!detalle) return;
    try { detalle = await Imagenologia.entregar(detalle.orden.id!); info = 'Orden entregada.'; await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  async function verInforme() {
    if (!detalle) return;
    try {
      const html = await Imagenologia.informe(detalle.orden.id!);
      const w = window.open('', '_blank');
      if (w) { w.document.open(); w.document.write(html); w.document.close(); }
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function cargarBase() {
    error = null; info = null;
    try {
      const r = await Imagenologia.seed();
      info = `Catálogo base: ${r.insertados} agregados, ${r.existentes} ya existían.`;
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function crearEstudio(e: SubmitEvent) {
    e.preventDefault();
    error = null;
    try {
      await Imagenologia.crearEstudio({ ...nuevoEx, precio: Number(nuevoEx.precio ?? 0) });
      nuevoEx = { codigo: '', nombre: '', modalidad: 'RAYOS_X', region: '', precio: 0 };
      await cargar();
      info = 'Estudio agregado al catálogo.';
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function guardarPrecio(e: EstudioCatalogo) {
    error = null;
    try {
      await Imagenologia.actualizarEstudio(e.codigo, { ...e, precio: Number(editPrecio) });
      editingCodigo = null;
      info = `Precio de ${e.nombre} actualizado.`;
      await cargar();
    } catch (err: any) { error = err?.message ?? String(err); }
  }

  const informable = $derived(
    detalle && ['SOLICITADA', 'EN_ADQUISICION', 'ADQUIRIDA'].includes(detalle.orden.estado ?? '')
  );

  $effect(() => { cargar(); });
</script>

<section>
  <header class="view-header">
    <div><h2>Imagenología</h2><p class="muted">Órdenes de imagen, adquisición e informes radiológicos</p></div>
    <button onclick={cargar} disabled={loading}>{loading ? '…' : 'Recargar'}</button>
  </header>
  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if info}<p class="ok">{info}</p>{/if}

  <div class="cols">
    <div>
      <div class="panel">
        <h3>Nueva orden</h3>
        <form onsubmit={crearOrden} class="stack">
          <label class="field"><span>Paciente *</span>
            <PacientePicker bind:value={nueva.pacienteId} onPick={(id, p) => { nueva.pacienteId = id; nueva.pacienteNombre = p ? `${p.nombres} ${p.apellidos}` : ''; }} placeholder="Buscar paciente…" />
          </label>
          <div class="row">
            <label class="field grow"><span>Médico solicitante</span>
              <input placeholder="Nombre del médico" bind:value={nueva.medico} />
            </label>
            <label class="field"><span>Prioridad</span>
              <select bind:value={nueva.prioridad}>
                <option value="RUTINA">Rutina</option>
                <option value="URGENTE">Urgente</option>
              </select>
            </label>
          </div>
          <div class="row">
            <label class="field grow"><span>Consultorio (opcional)</span>
              <select bind:value={nueva.consultorioId}>
                <option value="">— Consultorio (opcional) —</option>
                {#each consultorios as c (c.id)}<option value={c.id}>{c.nombre}</option>{/each}
              </select>
            </label>
            <label class="field grow"><span>Profesional (opcional)</span>
              <select bind:value={nueva.medicoId}>
                <option value="">— Profesional (opcional) —</option>
                {#each profesionales as p (p.id)}<option value={p.id}>{p.nombre}</option>{/each}
              </select>
            </label>
            <label class="field"><span>Pagador</span>
              <select bind:value={nueva.pagador}>
                <option value="PACIENTE">Paciente</option>
                <option value="CONSULTORIO">Consultorio</option>
                <option value="ASEGURADORA">Aseguradora</option>
              </select>
            </label>
          </div>
          <label class="field"><span>Indicación clínica</span>
            <input placeholder="Motivo del estudio" bind:value={nueva.indicacion} />
          </label>

          <div class="filter-bar">
            <input
              type="search"
              placeholder="Buscar estudio por nombre o código…"
              bind:value={busquedaEstudio}
            />
          </div>
          {#if puedeAgregarRapido}
            <div class="quick-add">
              <span>¿No aparece en la lista?</span>
              <button type="button" class="ghost sm" onclick={agregarEstudioRapido}>
                Agregar «{busquedaEstudio.trim()}» y seleccionar
              </button>
            </div>
          {/if}
          <div class="exams-scroll">
            <table class="exams-table">
              <thead><tr><th></th><th>Estudio</th><th>Modalidad</th><th class="num">Precio</th><th></th></tr></thead>
              <tbody>
                {#each catalogoFiltrado as e (e.id)}
                  <tr class:on={nueva.seleccion[e.codigo]}>
                    <td class="chk"><input type="checkbox" bind:checked={nueva.seleccion[e.codigo]} /></td>
                    <td>{e.nombre}</td>
                    <td>{(e.modalidad ?? '').replace('_', ' ')}</td>
                    <td class="num">
                      {#if editingCodigo === e.codigo}
                        <input type="number" step="0.01" bind:value={editPrecio} style="width:5.5rem" />
                      {:else}
                        Q{(e.precio ?? 0).toFixed(2)}
                      {/if}
                    </td>
                    <td>
                      {#if editingCodigo === e.codigo}
                        <button type="button" class="ghost sm" onclick={() => guardarPrecio(e)}>Guardar</button>
                      {:else}
                        <button type="button" class="link" onclick={() => { editingCodigo = e.codigo; editPrecio = e.precio ?? 0; }}>Editar</button>
                      {/if}
                    </td>
                  </tr>
                {:else}
                  <tr><td colspan="5" class="empty">Sin estudios en el catálogo. Usa "Cargar catálogo base" abajo.</td></tr>
                {/each}
              </tbody>
            </table>
          </div>

          <div class="paquetes">
            <button type="button" class="ghost sm" onclick={guardarPaqueteImg}>Guardar selección como paquete</button>
            {#each paquetesImg as p (p.nombre)}
              <button type="button" class="chip" onclick={() => aplicarPaqueteImg(p)}>{p.nombre}</button>
            {/each}
          </div>

          <div class="row between">
            <span class="total">{seleccionados.length} estudio(s) · Total Q{totalEstimado.toFixed(2)}</span>
            <button type="submit">Crear orden</button>
          </div>
        </form>
      </div>

      <div class="panel">
        <h3>Órdenes recientes</h3>
        <table>
          <thead><tr><th>N°</th><th>Paciente</th><th>Estado</th></tr></thead>
          <tbody>
            {#each ordenes as o (o.id)}
              <tr class:sel={detalle?.orden.id === o.id} onclick={() => abrir(o.id!)}>
                <td>{o.numero}</td>
                <td>{o.pacienteNombre}</td>
                <td><span class="badge b-{o.estado}" title={estadoImagenHint(o.estado)}>{estadoImagenLabel(o.estado)}</span></td>
              </tr>
            {:else}<tr><td colspan="3" class="empty">Sin órdenes.</td></tr>{/each}
          </tbody>
        </table>
      </div>
    </div>

    <div>
      {#if detalle}
        {@const o = detalle.orden}
        <div class="panel">
          <h3>Orden {o.numero} <span class="badge b-{o.estado}" title={estadoImagenHint(o.estado)}>{estadoImagenLabel(o.estado)}</span></h3>
          <p class="meta">{o.pacienteNombre}{o.medicoSolicitante ? ` · ${o.medicoSolicitante}` : ''} · Total Q{(o.total ?? 0).toFixed(2)}</p>
          {#if o.indicacionClinica}<p class="meta"><strong>Indicación:</strong> {o.indicacionClinica}</p>{/if}

          <table class="res">
            <thead><tr><th>Estudio</th><th>Modalidad</th><th>Región</th></tr></thead>
            <tbody>
              {#each detalle.estudios as es (es.id)}
                <tr><td>{es.estudioNombre}</td><td>{(es.modalidad ?? '').replace('_', ' ')}</td><td>{es.region ?? '—'}</td></tr>
              {/each}
            </tbody>
          </table>

          {#if o.estado === 'SOLICITADA' || o.estado === 'EN_ADQUISICION'}
            <div class="acciones"><button onclick={adquirir}>Marcar imágenes adquiridas</button></div>
          {/if}
        </div>

        {#if informable}
          <div class="panel">
            <h3>Informe radiológico</h3>
            <div class="stack">
              <label class="field"><span>Técnica / protocolo</span>
                <input placeholder="Técnica / protocolo" bind:value={informe.tecnica} />
              </label>
              <label class="field"><span>Hallazgos *</span>
                <textarea placeholder="Hallazgos" rows="4" bind:value={informe.hallazgos}></textarea>
              </label>
              <label class="field"><span>Impresión diagnóstica *</span>
                <textarea placeholder="Impresión diagnóstica" rows="2" bind:value={informe.impresion}></textarea>
              </label>
              <label class="field"><span>Recomendaciones</span>
                <textarea placeholder="Recomendaciones" rows="2" bind:value={informe.recomendaciones}></textarea>
              </label>
              <label class="field"><span>Enlace al visor / PACS</span>
                <input placeholder="Enlace al visor / PACS" bind:value={informe.imagenesUrl} />
              </label>
              <button onclick={firmarInforme}>Firmar informe</button>
            </div>
          </div>
        {/if}

        <div class="acciones">
          {#if o.codigoValidacion}
            <button class="ghost" onclick={verInforme}>Ver informe</button>
            {#if o.estado === 'INFORMADA'}<button class="ghost" onclick={entregar}>Marcar entregada</button>{/if}
          {/if}
          {#if o.imagenesUrl}<a class="ghost link-btn" href={o.imagenesUrl} target="_blank" rel="noopener">Abrir imágenes</a>{/if}
        </div>

        {#if o.codigoValidacion}
          <div class="folio">
            Folio de verificación: <strong>{o.codigoValidacion}</strong>{#if o.radiologo} · Informado por {o.radiologo}{/if}
            {#if o.cuentaId}<br><span class="cargo">Cargado a la cuenta del paciente (Q{(o.total ?? 0).toFixed(2)})</span>{/if}
          </div>
        {/if}
      {:else}
        <div class="panel">
          <p class="hint">Selecciona o crea una orden para adquirir imágenes, redactar el informe y verificarlo.</p>
        </div>
      {/if}
    </div>
  </div>

  <div class="panel">
    <h3 class="cat-head">
      <button class="link" onclick={() => mostrarCatalogo = !mostrarCatalogo}>{mostrarCatalogo ? '▾' : '▸'} Catálogo de estudios ({catalogo.length})</button>
      <button type="button" class="ghost sm" onclick={cargarBase}>Cargar catálogo base</button>
    </h3>
    {#if mostrarCatalogo}
      <div class="filter-bar">
        <input
          type="search"
          placeholder="Buscar estudio por nombre o código para agregar o editar precio…"
          bind:value={busquedaEstudio}
        />
      </div>
      {#if puedeAgregarRapido}
        <div class="quick-add">
          <span>¿No aparece en el catálogo?</span>
          <button type="button" class="ghost sm" onclick={agregarEstudioRapido}>
            Agregar «{busquedaEstudio.trim()}» al catálogo
          </button>
        </div>
      {/if}
      <form onsubmit={crearEstudio} class="form-grid">
        <label class="field"><span>Código</span><input placeholder="Código" bind:value={nuevoEx.codigo} required /></label>
        <label class="field"><span>Nombre</span><input placeholder="Nombre" bind:value={nuevoEx.nombre} required /></label>
        <label class="field"><span>Modalidad</span>
          <select bind:value={nuevoEx.modalidad}>
            {#each MODALIDADES as m}<option value={m}>{m.replace('_', ' ')}</option>{/each}
          </select>
        </label>
        <label class="field"><span>Región</span><input placeholder="Región" bind:value={nuevoEx.region} /></label>
        <label class="field"><span>Precio (Q)</span><input type="number" step="any" placeholder="0.00" bind:value={nuevoEx.precio} /></label>
        <button type="submit">+ Estudio</button>
      </form>
      <table>
        <thead><tr><th>Código</th><th>Nombre</th><th>Modalidad</th><th>Región</th><th class="num">Precio</th><th>Acciones</th></tr></thead>
        <tbody>
          {#each catalogoFiltrado as e (e.id)}
            <tr>
              <td>{e.codigo}</td>
              <td>{e.nombre}</td>
              <td>{e.modalidad.replace('_', ' ')}</td>
              <td>{e.region ?? '—'}</td>
              <td class="num">
                {#if editingCodigo === e.codigo}
                  <input type="number" step="any" class="edit-precio" bind:value={editPrecio} />
                {:else}
                  Q{(e.precio ?? 0).toFixed(2)}
                {/if}
              </td>
              <td>
                {#if editingCodigo === e.codigo}
                  <button type="button" class="ghost sm" onclick={() => guardarPrecio(e)}>Guardar</button>
                  <button type="button" class="link" onclick={() => editingCodigo = null}>Cancelar</button>
                {:else}
                  <button type="button" class="link" onclick={() => { editingCodigo = e.codigo; editPrecio = e.precio ?? 0; }}>Editar precio</button>
                {/if}
              </td>
            </tr>
          {:else}<tr><td colspan="6" class="empty">Catálogo vacío.</td></tr>{/each}
        </tbody>
      </table>
    {/if}
  </div>
</section>

<style>
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  button { padding: .5rem .9rem; border: 0; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  button:hover:not(:disabled) { transform: translateY(-1px); }
  button:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
  h3 { margin: 0 0 .5rem; font-size: 1rem; color: #166534; }
  .panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 1.25rem; margin: 0 0 1rem; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .cols { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; align-items: start; }
  @media (max-width: 860px) { .cols { grid-template-columns: 1fr; } }
  .stack { display: flex; flex-direction: column; gap: .6rem; }
  .row { display: flex; gap: .6rem; align-items: flex-end; flex-wrap: wrap; }
  .row.between { justify-content: space-between; align-items: center; }
  .field { display: flex; flex-direction: column; gap: .3rem; }
  .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .grow { flex: 1; min-width: 140px; }
  input, select, textarea { padding: .5rem .65rem; border-radius: 8px; border: 1px solid #e5e7eb; font: inherit; background: #fff; color: #111827; }
  input:focus, select:focus, textarea:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  textarea { resize: vertical; }
  .exams-scroll { max-height: 360px; overflow-y: auto; border: 1px solid #e5e7eb; border-radius: 8px; }
  .filter-bar { margin-bottom: .45rem; }
  .filter-bar input { width: 100%; box-sizing: border-box; }
  .quick-add { display: flex; flex-wrap: wrap; gap: .5rem; align-items: center; margin: .35rem 0 .65rem; padding: .5rem .65rem; background: #f0fdf4; border-radius: 8px; font-size: .88rem; }
  .paquetes { display: flex; flex-wrap: wrap; gap: .4rem; align-items: center; margin: .55rem 0 .25rem; }
  .chip { font-size: .78rem; padding: .28rem .55rem; border-radius: 6px; border: 1px solid #e5e7eb; background: #fff; color: #166534; cursor: pointer; font-weight: 500; box-shadow: none; }
  .chip:hover { background: #f0fdf4; border-color: #16a34a; transform: none; }
  .exams-table { margin-top: 0; }
  .exams-table thead th { position: sticky; top: 0; z-index: 1; }
  .exams-table td { font-size: .85rem; }
  .exams-table td.chk { width: 2.2rem; text-align: center; }
  .exams-table input[type=checkbox] { accent-color: #16a34a; }
  tr.on { background: #f0fdf4; }
  .total { font-size: .85rem; color: #166534; font-weight: 600; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .55rem .8rem; background: #f0fdf4; }
  td { padding: .45rem .8rem; border-top: 1px solid #f3f4f6; text-align: left; font-size: .88rem; color: #111827; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  tbody tr { cursor: pointer; }
  tbody tr.sel { background: #f0fdf4; }
  .res tbody tr, .exams-table tbody tr { cursor: default; }
  .meta { color: #6b7280; font-size: .85rem; margin: .2rem 0 .4rem; }
  .acciones { display: flex; gap: .5rem; margin-top: .75rem; flex-wrap: wrap; align-items: center; }
  .ghost { background: #fff; color: #16a34a; border: 1px solid #16a34a; cursor: pointer; font-weight: 600; box-shadow: none; }
  .ghost:hover { background: #f0fdf4; }
  .link-btn { text-decoration: none; padding: .45rem .6rem; border-radius: 8px; font-size: .9rem; }
  .folio { margin-top: .6rem; background: #f0fdf4; border: 1px solid #86efac; border-radius: 8px; padding: .5rem .65rem; font-size: .85rem; color: #166534; }
  .cargo { color: #15803d; font-weight: 600; }
  .form-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: .6rem; margin-bottom: .6rem; align-items: end; }
  .form-grid > button { align-self: end; }
  .badge { font-size: .68rem; padding: .1rem .45rem; border-radius: 999px; font-weight: 600; }
  .b-SOLICITADA { background: #f3f4f6; color: #374151; }
  .b-EN_ADQUISICION { background: #fef3c7; color: #92400e; }
  .b-ADQUIRIDA { background: #dcfce7; color: #166534; }
  .b-INFORMADA { background: #bbf7d0; color: #14532d; }
  .b-ENTREGADA { background: #ecfdf5; color: #15803d; }
  .b-ANULADA { background: #fee2e2; color: #dc2626; }
  .cat-head { display: flex; align-items: center; gap: .75rem; margin: 0; }
  .link { background: transparent; border: 0; color: #16a34a; cursor: pointer; font: inherit; padding: 0; box-shadow: none; }
  .link:hover { transform: none; }
  .ghost.sm { padding: .25rem .6rem; font-size: .8rem; border-radius: 8px; }
  .edit-precio { width: 5.5rem; padding: .3rem .4rem; }
  .empty, .hint { color: #9ca3af; font-style: italic; font-size: .85rem; }
  .err { color: #dc2626; }
  .ok { color: #166534; background: #dcfce7; padding: .35rem .55rem; border-radius: 8px; }
</style>
